// CANVAS-VOICE: the Live canvas's "Voice" switch, wired to the voice service (services/voice),
// served on the app's origin under `/voice` (VITE_VOICE_URL overrides). The person's own session
// cookie goes with every call; the voice service forwards it to the API, so what the front reads
// and shows is the person's own (permissions, flags). Its `show_user` lands on this Live session
// (`live_session_id`): the canvas updates over the live channel like any other op.
//
//   1. GET /healthz: which providers are configured (names only, never values);
//   2. every audio slot set → a voice session: the LiveKit room's url and token come back and the
//      browser joins it (livekit-client, loaded only then), microphone on;
//   3. anything missing → a text session in the same panel, and the notice names what is
//      missing ("voice not configured: LIVEKIT_URL missing, …").

export type VoiceStatus = {
  voice: "configured" | "not_configured";
  text: "llm" | "rules";
  missing: string[];
  message: string;
};

/**
 * LANE-C: one write the voice front made (a card, a correction, a status change) and where it
 * stands. Keyed by its client key, so a replayed turn shows the same row, never a second.
 */
export type VoiceCommandStatus =
  | "accepted"
  | "queued"
  | "applied"
  | "failed"
  | "awaiting_confirmation"
  | "cancelled";

export type VoiceCommand = {
  key: string;
  kind: "create_task" | "steer" | "status_change";
  status: VoiceCommandStatus;
  taskId: string | null;
  taskNumber: number | null;
  delivery: "running_turn" | "next_turn" | null;
  detail: string;
  replayed: boolean;
  at: string;
  request?: { state: string; done: boolean; stuck: boolean } | null;
};

export type VoiceFocus = { id: string; number: number | null } | null;

export type VoiceState = {
  focus: VoiceFocus;
  pending: { task_id: string; task_number: number; from: string; to: string } | null;
  commands: VoiceCommand[];
};

export type VoiceTurn = {
  reply: string;
  tools: { name: string; ok: boolean; say: string }[];
  interrupted: boolean;
  /** LANE-C: the same words again came back as the first outcome. */
  replayed?: boolean;
  commands?: VoiceCommand[];
  focus?: VoiceFocus;
};

/** The commands, newest outcome per key, in the order they were first made. */
export function mergeCommands(list: VoiceCommand[], more: VoiceCommand[]): VoiceCommand[] {
  const by = new Map(list.map((c) => [c.key, c]));
  for (const c of more) by.set(c.key, c);
  return [...by.values()].sort((a, b) => a.at.localeCompare(b.at));
}

export type VoiceSession = {
  id: string;
  mode: "voice" | "text";
  /** Why this is text, by name ("voice not configured: … missing"); null in voice mode. */
  notice: string | null;
  /** LANE-C: the session came back (reload, reconnect) with what it already did. */
  resumed: boolean;
  state: VoiceState;
  /**
   * Text mode (and typing in voice mode): one turn. `focusTaskId` is the card the page has in
   * focus, so typing keeps the target the voice turns had.
   */
  say: (text: string, focusTaskId?: string | null) => Promise<VoiceTurn>;
  /** LANE-C: re-reads the commands' status (and lane B's request state). */
  refresh: () => Promise<VoiceState>;
  /** Ends the session (and leaves the room). */
  stop: () => void;
};

export type VoiceStartInput = {
  companyId: string;
  /** The Live session (this tab): where the front's canvas ops land. */
  sessionId: string;
  deMembershipId: string;
  deSeatId: string;
  deName?: string;
  boardId: string;
};

/** Joins a LiveKit room with the microphone on; returns how to leave it. */
export type RoomJoiner = (room: { url: string; token: string }) => Promise<() => void>;

export type VoiceDeps = {
  /** The voice service root (default `/voice`, or VITE_VOICE_URL). */
  baseUrl?: string;
  fetch?: typeof fetch;
  join?: RoomJoiner;
};

export class VoiceStartError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "VoiceStartError";
  }
}

const envBase = (): string => {
  const v = (import.meta as { env?: Record<string, unknown> }).env?.VITE_VOICE_URL;
  return typeof v === "string" && v ? v : "/voice";
};

/** The default joiner: livekit-client, imported only when a voice session starts. */
export const livekitJoin: RoomJoiner = async ({ url, token }) => {
  const { Room } = await import("livekit-client");
  const room = new Room({ adaptiveStream: true, dynacast: true });
  await room.connect(url, token);
  await room.localParticipant.setMicrophoneEnabled(true);
  return () => {
    void room.disconnect();
  };
};

const readMessage = async (r: Response) => {
  try {
    const b = (await r.json()) as { code?: string; message?: string };
    return { code: b.code ?? `http_${r.status}`, message: b.message ?? b.code ?? r.statusText };
  } catch {
    return { code: `http_${r.status}`, message: r.statusText };
  }
};

/**
 * LIVE-WORKSPACE: the voice service's status (`/healthz`: provider names only, never values), so
 * the Live page says "voice not configured" before anyone presses Start. Throws
 * `VoiceStartError("voice_unreachable")` when the service does not answer.
 */
export async function readVoiceStatus(deps: VoiceDeps = {}): Promise<VoiceStatus> {
  const base = (deps.baseUrl ?? envBase()).replace(/\/$/, "");
  const f = deps.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  try {
    const h = await f(`${base}/healthz`, { credentials: "include" });
    if (!h.ok) throw new Error(String(h.status));
    return ((await h.json()) as { status: VoiceStatus }).status;
  } catch {
    throw new VoiceStartError("voice_unreachable", "The voice service is not reachable.");
  }
}

/**
 * Starts a voice (or, with keys missing, text) session for the Live session `sessionId`. Throws
 * `VoiceStartError` when the service refuses (voice off for the company, not a member, down).
 */
export async function startVoiceSession(
  input: VoiceStartInput,
  deps: VoiceDeps = {},
): Promise<VoiceSession> {
  const base = (deps.baseUrl ?? envBase()).replace(/\/$/, "");
  const f = deps.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const json = { "content-type": "application/json" };
  const status = await readVoiceStatus({ ...deps, baseUrl: base });
  const mode = status.voice === "configured" ? "voice" : "text";
  const started = await f(`${base}/v1/sessions`, {
    method: "POST",
    credentials: "include",
    headers: json,
    body: JSON.stringify({
      company_id: input.companyId,
      de_membership_id: input.deMembershipId,
      de_seat_id: input.deSeatId,
      ...(input.deName ? { de_name: input.deName } : {}),
      board_id: input.boardId,
      live_session_id: input.sessionId,
      mode,
    }),
  });
  if (!started.ok) {
    const m = await readMessage(started);
    throw new VoiceStartError(m.code, m.message);
  }
  const s = (await started.json()) as {
    session_id: string;
    mode: "voice" | "text";
    resumed?: boolean;
    state?: VoiceState;
    livekit?: { url: string; room: string; token: string };
  };
  let leave: (() => void) | null = null;
  if (s.mode === "voice" && s.livekit) leave = await (deps.join ?? livekitJoin)(s.livekit);
  let stopped = false;
  return {
    id: s.session_id,
    mode: s.mode,
    notice: s.mode === "voice" ? null : status.message,
    resumed: s.resumed === true,
    state: s.state ?? { focus: null, pending: null, commands: [] },
    async say(text, focusTaskId) {
      const r = await f(`${base}/v1/sessions/${s.session_id}/turn`, {
        method: "POST",
        credentials: "include",
        headers: json,
        body: JSON.stringify({
          text,
          via: "text",
          ...(focusTaskId ? { focus_task_id: focusTaskId } : {}),
        }),
      });
      if (!r.ok) {
        const m = await readMessage(r);
        throw new VoiceStartError(m.code, m.message);
      }
      return (await r.json()) as VoiceTurn;
    },
    async refresh() {
      const r = await f(`${base}/v1/sessions/${s.session_id}`, { credentials: "include" });
      if (!r.ok) {
        const m = await readMessage(r);
        throw new VoiceStartError(m.code, m.message);
      }
      return (await r.json()) as VoiceState;
    },
    stop() {
      if (stopped) return;
      stopped = true;
      leave?.();
      void f(`${base}/v1/sessions/${s.session_id}`, {
        method: "DELETE",
        credentials: "include",
      }).catch(() => undefined);
    },
  };
}
